import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Template} from "../../../../components/template/template.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import InputCheckbox from "../../../../components/input/input-checkbox.jsx";
import {Card} from "../../../../components/card/card.jsx";
import Button from "../../../../components/button/button.jsx";
import SelectComponent from "../../../../components/select/select-component.jsx";
import {FaEraser, FaExclamationTriangle, FaFileAlt, FaFileExport, FaFileImport, FaPlus, FaSearch} from "react-icons/fa";
import groupService from "../../../../services/web/group-service.js";
import {useQuery} from "@tanstack/react-query";
import projectUtils from "../../../../utils/project-utils.js";
import {AgGridReact} from "ag-grid-react";
import userService from "../../../../services/web/user-service.js";
import aggridUtils from "../../../../utils/aggrid-utils.js";
import ImageCellRenderer from "./components/image-cell-renderer.jsx";
import ListObjectsCellRenderer from "./components/list-objects-cell-renderer.jsx";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import ImportData from "./components/import-modal.jsx";
import {handleFilters} from "../components/handle-filters.js";
import SentryRow from "../../../../components/template/sentry-row.jsx";
import vmsService from "../../../../services/web/vms-service.js";
import ShowHideColumnsGrid from "../../../../components/show-hide-columns-grid/show-hide-columns-grid.jsx";
import {exportXlsx} from "../../../../utils/excel-export-utils.js";
import {addDays, endOfDay, format, isAfter, isWithinInterval, startOfDay} from "date-fns";
import {FaCircleExclamation} from "react-icons/fa6";

const statusOptions = [
    {value: "active", label: "Ativo"},
    {value: "inactive", label: "Inativo"},
]

const initialFilter = {
    namewar: "",
    name: "",
    login: "",
    loginBlocked: false,
    status: "active",
    cpf: "",
    groups: [],
    accessGroupsVms: []
}

const storageItem = "users-hidden-columns"

const AgentsUserList = () => {
    const navigate = navigateHook();

    const [users, setUsers] = useState([]);
    const [filter, setFilter] = useState(initialFilter);
    const [usersFiltered, setUsersFiltered] = useState([]);
    const [columnDefs, setColumnDefs] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [importModal, setImportModal] = useState(false);

    const gridRef = useRef(null);

    const onColumnMoved = (params) => {
        const columns = params.api.getAllDisplayedColumns().map(col => col.colId);
        localStorage.setItem("users-order-columns", JSON.stringify(columns))
    }

    const onChange = ({target}) => {
        let {id, value, checked, type} = target;

        if (type === "checkbox") value = checked;
        setFilter(prev => ({...prev, [id]: value}));
    }

    const onColumnVisible = ({source, column}) => {
        if (source === "uiColumnMoved") toggleColumn(column.colDef.field)
    }

    const toggleColumn = (field) => {
        setColumnDefs((prevDefs) =>
            prevDefs.map((col) => col.field === field ? {...col, hide: !col.hide} : col)
        );
    }

    const {data, status, refetch: refetchList} = useQuery({
        queryFn: async () => {
            const {data} = await userService.list();
            return data ?? [];
        },
        queryKey: ['users'],
    });

    useEffect(() => {
        if (status === 'success') {
            gridRef.current?.api?.showLoadingOverlay();
            setUsers(data)

            setUsersFiltered(handleFilters(data, filter));
        }
    }, [data, status]);

    const { data: vmsConfigurations } = useQuery({
        queryFn: () => vmsService.getAll(),
        queryKey: ["vmsConfigurations"]
    });

    const { data: groups = [] } = useQuery({
        queryFn: async () => groupService.getOptions(),
        queryKey: ['groups'],
    });

    const loadAccessGroups = async (search) => {
        if (!vmsConfigurations || !search) return [];

        const data = await vmsService.getAccessGroups(search);

        return data.map(({ vmsConfigurationId, groupName }) => {
            const vms = vmsConfigurations?.find(
                v => v.id === vmsConfigurationId
            );

            return {
                value: groupName,
                label: `${vms?.vmsCorporationName ?? ''} - ${groupName}`
            };
        });
    };

    const onCellClicked = ({data, event, colDef}) => {
        let isBlank = false;
        let urlDev = `/user-agents/form/${data.id}`;
        
        if (colDef.field === "open-history") {
            urlDev = `/reports/audit/?userHistory=${data.id}`
        } else if (event.ctrlKey) {
            isBlank = true
        }

        navigate(urlDev, isBlank);
    }

    const handleKeyDown = ({key}) => {
        if (key === 'Enter')  apply(filter);
        if (key === 'Escape') clearFilter()
    }

    const clearFilter = () => {
        setFilter(initialFilter);
        apply(initialFilter);
    }

    const apply = (currentFilter) => {
        gridRef.current?.api?.showLoadingOverlay();
        setUsersFiltered(handleFilters(users, currentFilter));
    }

    const theme = useMemo(() => {
        return aggridUtils.generalTheme();
    }, []);

    useEffect(() => {
        const vmsAccessGroup = (value) => {
            if (!value) return null;

            return value
                .flatMap(e => e?.userAccessGroups || [])
                .map(g => g?.accessGroup?.groupName)
                .filter(Boolean)
                .join(' | ');
        }

        const columns = [
            {
                headerName: "Foto", field: "imageId", width: 50, sortable: false,
                cellRenderer: ImageCellRenderer
            },
            {
                headerName: "Nome de guerra", field: "namewar", sort: "asc",
                comparator: (a, b, nodeA, nodeB, isInverted) => {
                    return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
                }

            },
            {headerName: "Nome", field: "name", flex: 1, minWidth: 150,
                comparator: (a, b, nodeA, nodeB, isInverted) => {
                    return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
                }
            },
            {headerName: "Login", field: "login",
                comparator: (a, b, nodeA, nodeB, isInverted) => {
                    return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
                }
            },
            {
                headerName: "Status", field: "status", width: 100,
                cellRenderer: ({value}) => value ? "Ativo" : "Inativo"
            },
            {headerName: "Grupo de operação", field: "groups", cellRenderer: ListObjectsCellRenderer,
                comparator: (a, b, nodeA, nodeB, isInverted) => {
                    a = a ? a.map(item => item.name).join(' | ') : ""
                    b = b ? b.map(item => item.name).join(' | ') : ""
                    return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
                }
            },
            {headerName: "Grupo de acesso (VMS)", field: "vmsUserConfiguration",
                valueFormatter: ({ value }) => vmsAccessGroup(value),
                comparator: (a, b, nodeA, nodeB, isInverted) => {
                    a = a ? vmsAccessGroup(a) : ""
                    b = b ? vmsAccessGroup(b) : ""
                    return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
                }
            },
            {headerName: "Função", field: "roles", cellRenderer: ListObjectsCellRenderer,
                comparator: (a, b, nodeA, nodeB, isInverted) => {
                    a = a ? a.map(item => item.name).join(' | ') : ""
                    b = b ? b.map(item => item.name).join(' | ') : ""
                    return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
                }
            },
            {
                headerName: "Último login", field: "lastLogin", width: 150,
                valueFormatter: ({value}) => projectUtils.formatDate(value),
                comparator: (a, b, nodeA, nodeB, isInverted) => {
                    return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
                }
            },
            {
                headerName: "Última troca de senha", field: "dateChangePassword", width: 180,
                valueFormatter: ({value}) => projectUtils.formatDate(value),
                comparator: (a, b, nodeA, nodeB, isInverted) => {
                    return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
                }
            },
            {
                headerName: "Login bloqueado", field: "loginBlocked", width: 130,
                cellRenderer: ({value}) => value ? "Sim" : "Não"
            },
            {
                headerName: "Prazo contratual", field: "profile.contractualTerm", width: 150,
                valueFormatter: ({value}) => projectUtils.formatDate(value, "DD/MM/YYYY"),
                comparator: aggridUtils.nullLastComparator,
                cellRenderer: ({value, valueFormatted}) => {
                    if (!value) return "";

                    const todayStart = startOfDay(new Date());
                    const givenDateEnd = value ? endOfDay(value) : null
                    const almostDate = addDays(todayStart, 45);

                    if (givenDateEnd) {
                        if (!isAfter(givenDateEnd, todayStart)) {
                            return (
                                <span className="flex items-center gap-1 text-[#dc4c64]" title="Prazo contratual vencido">
                                    <FaExclamationTriangle />
                                    {valueFormatted}
                                </span>
                            )
                        } else if (isWithinInterval(givenDateEnd, {start: todayStart, end: almostDate})) {
                            return (
                                <span className="flex items-center gap-1 text-[#FC6D00]" title="Prazo contratual próximo do vencimento">
                                    <FaCircleExclamation />
                                    {valueFormatted}
                                </span>
                            )
                        }
                    }

                    return valueFormatted
                }
            },
            {
                headerName: "Histórico", field: "open-history", width: 80, sortable: false,
                comparator: (a, b, nodeA, nodeB, isInverted) => {
                    return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
                },
                cellRenderer: () => {
                    return <div className="flex gap-1 justify-center items-center h-full w-full">
                        <Button className={"!h-[25px] !w-[30px] !p-0"}>
                            <FaFileAlt />
                        </Button>
                    </div>
                }
            }
        ];

        const defaultHiddenColumns = ["profile.contractualTerm"];
        const hiddenColumns = JSON.parse(localStorage.getItem("users-hidden-columns")) ?? defaultHiddenColumns;
        const orderColumns = JSON.parse(localStorage.getItem("users-order-columns"));

        if (hiddenColumns) columns.forEach(c => hiddenColumns.includes(c.field) ? c.hide = true : "");
        if (orderColumns) aggridUtils.sortColumns(columns, orderColumns);

        setColumnDefs(columns);
    }, []);

    const exportData = async () => {
        const roles = new Set();

        const normalizedUsers = usersFiltered.map(user => {
            const roleString = user.role || "Sem função";
            roleString.split("|").forEach(role => roles.add(role.trim()));
            return { ...user, role: roleString };
        });

        const truncateString = (str) => str.length > 28 ? str.slice(0, 28) + '...' : str;

        const usersByRole = Array.from(roles).reduce((acc, role) => {
            acc[role] = normalizedUsers
                .filter(user => user.role.includes(role))
                .map(user => ({
                    "Nome de guerra": user.namewar,
                    "Nome": user.name,
                    "Login": user.login,
                    "Status": user.status ? "Ativo" : "Inativo",
                    "Grupo de operação": user.group,
                    "Último Login": projectUtils.formatDate(user.lastLogin),
                    "Última troca de senha": projectUtils.formatDate(user.dateChangePassword),
                    "Login bloqueado": user.loginBlocked ? "Sim" : "Não"
                }));
            return acc;
        }, {});

        await exportXlsx({
            fileName: "agentes.xlsx",
            sheets: Object.keys(usersByRole)
                .sort((a, b) => a.localeCompare(b))
                .map(role => ({
                    name: truncateString(role),
                    data: usersByRole[role]
                }))
        });
    }

    const newAgent = () => {
        const urlDev = "/user-agents/form"
        navigate(urlDev);
    }

    return (
        <>
            <Template isLoading={isLoading}>
                <Template.Header>
                    <Template.Title>
                        Agentes
                    </Template.Title>

                    <div className={"flex gap-2"}>
                        <Button target={"success"} onClick={() => setImportModal(true)}>
                            <FaFileImport /> Importar
                        </Button>
                        <Button target={"success"} onClick={exportData}>
                            <FaFileExport /> Exportar
                        </Button>

                        <Button onClick={newAgent}>
                            <FaPlus /> Adicionar agente
                        </Button>
                    </div>
                </Template.Header>

                <Template.Body>
                    <Card>
                        <Card.Body>
                            <SentryRow className={"!gap-x-4 !gap-y-3"}>
                                <div className="col-span-3">
                                    <InputForm
                                        id="namewar"
                                        label="Nome de guerra"
                                        value={filter.namewar}
                                        onChange={onChange}
                                        onKeyDown={handleKeyDown}
                                    />
                                </div>
                                <div className="col-span-4">
                                    <InputForm
                                        id="name"
                                        label="Nome"
                                        value={filter.name}
                                        onChange={onChange}
                                        onKeyDown={handleKeyDown}
                                    />
                                </div>
                                <div className="col-span-5 flex justify-center gap-4">
                                    <InputForm
                                        classNameWrapper={"w-full"}
                                        id="login"
                                        value={filter.login}
                                        onChange={onChange}
                                        onKeyDown={handleKeyDown}
                                        label="Login"/>
                                    <InputCheckbox
                                        className={"w-50"}
                                        id="loginBlocked"
                                        label="Login bloqueado"
                                        checked={filter.loginBlocked}
                                        onChange={onChange}
                                    />
                                </div>

                                <div className="col-span-3">
                                    <SelectComponent
                                        id="status"
                                        value={statusOptions.find(s => s.value === filter.status)}
                                        options={statusOptions}
                                        label={"Status"}
                                        onChange={(selected) =>
                                            setFilter(prev => ({...prev, status: selected?.value ?? ""}))
                                        }
                                    />
                                </div>
                                <div className="col-span-2">
                                    <InputForm
                                        id="cpf"
                                        label="CPF"
                                        value={filter.cpf}
                                        onChange={onChange}
                                        mask={'000.000.000-00'}
                                        onKeyDown={handleKeyDown}
                                    />
                                </div>
                                <div className="col-span-3">
                                    <SelectComponent
                                        id="accessGroupsVms"
                                        label="Grupo de acesso (VMS)"
                                        value={filter.accessGroupsVms}
                                        defaultOptions={[]}
                                        loadOptions={loadAccessGroups}
                                        cacheOptions
                                        isAsync={true}
                                        isMulti
                                        classNameWrapper={"w-full"}
                                        onChange={(selected) =>
                                            setFilter(prev => ({ ...prev, accessGroupsVms: selected ?? [] }))
                                        }
                                    />
                                </div>
                                <div className="col-span-4 flex gap-4">
                                    <SelectComponent
                                        classNameWrapper={"w-full"}
                                        label="Grupo de operação"
                                        id="groups"
                                        options={groups}
                                        isMulti
                                        value={filter.groups}
                                        onChange={(selected) =>
                                            setFilter(prev => ({...prev, groups: selected ?? []}))
                                        }
                                    />
                                    <div className="flex justify-start gap-2">
                                        <Button target={"primary-outline"} title="Limpar" onClick={() => clearFilter()}>
                                            <FaEraser />
                                        </Button>
                                        <Button title="Buscar" onClick={() => apply(filter)}>
                                            <FaSearch />
                                        </Button>
                                    </div>
                                </div>
                            </SentryRow>
                        </Card.Body>
                    </Card>

                    <Card className={"mt-2"}>
                        <Card.Body>
                            <div className={"aggrid-row-pointer h-[calc(100vh-197px)]"}>
                                <AgGridReact
                                    ref={gridRef}
                                    rowData={usersFiltered}
                                    columnDefs={columnDefs}
                                    onColumnMoved={onColumnMoved}
                                    onColumnVisible={onColumnVisible}
                                    pagination
                                    paginationPageSize={100}
                                    paginationPageSizeSelector={[20, 50, 100, 500]}
                                    animateRows={false}
                                    suppressCellFocus={true}
                                    onCellClicked={onCellClicked}
                                    gridOptions={{localeText: aggridUtils.localization()}}
                                    theme={theme}
                                />
                            </div>

                            <ShowHideColumnsGrid
                                columnDefs={columnDefs}
                                toggleColumn={toggleColumn}
                                storageItem={storageItem}
                            />
                        </Card.Body>
                    </Card>
                </Template.Body>
            </Template>
            <ImportData openModal={setImportModal} modal={importModal} refetch={refetchList} setIsLoading={setIsLoading}/>
        </>
    );
};

export default AgentsUserList;
